/*
BoxWine is a custom component that displays the details of a wine record in a box format. It is used in the Provenance module to show the details of a wine record. 

The component receives a record prop and displays the name, producer, region, vintage, alcohol, type, style, product ID, batch number, SKU, and description of the wine. The component uses the Box, Typography, and Chip components from the Material-UI library to display the details. 

The Box component is used to create a box layout for the wine details. The Typography component is used to display the title and description of the wine. The Chip component is used to display the vintage, alcohol, type, and style of the wine. 

The component is used in the ProvenanceList component in the Provenance module to display the details of a wine record. 
*/

import React from 'react';
import { useTheme, Typography, Chip, Box } from '@mui/material';
import { TextInput, SelectInput } from 'react-admin';
import { required } from 'ra-core';

/**
 * Wine product box component
 * @component
 * @example
 * // Example record structure:
 * const wineRecord = {
 *   id: "W123",
 *   name: "Château Margaux 2015",
 *   producer: "Château Margaux",
 *   region: "Bordeaux",
 *   vintage: "2015",
 *   alcohol: "13.5",
 *   type: "Red",
 *   style: "Full-bodied",
 *   batchNumber: "BN456",
 *   sku: "SKU789",
 *   description: "Complex bouquet with notes of dark berries...",
 *   image: "url/to/wine/image.jpg"
 * };
 */

export const defaultWineImage = "https://placehold.co/200x300?text=Wine&font=playfair-display";

const BoxWine = ({ record }) => {
    const theme = useTheme();
    return (
        <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                {record.name || 'Unnamed Wine'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Producer:</strong> {record?.producer || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Region:</strong> {record?.region || 'Unknown'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip label={record?.vintage || 'N/A'} size="small" color="primary" variant="outlined" />
                <Chip label={record?.alcohol ? `${record.alcohol}%` : 'N/A'} size="small" color="secondary" variant="outlined" />
                <Chip label={record?.type || 'N/A'} size="small" />
                <Chip label={record?.style || 'N/A'} size="small" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Typography variant="body2"><strong>Product ID:</strong> {record.id || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Batch Number:</strong> {record?.batchNumber || 'N/A'}</Typography>
                <Typography variant="body2"><strong>SKU:</strong> {record?.sku || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <strong>Description:</strong> {record?.description || 'N/A'}
                </Typography>
            </Box>
        </Box>
    );
};

export const renderWineFields = () => (
    <>
      <TextInput label="Wine" source="data.name" validate={required()} />
      <TextInput label="Batch #" source="data.batchNumber" validate={required()} />
      <TextInput label="SKU" source="data.sku" validate={required()} />
      <TextInput label="Price per bottle" source="data.price" validate={required()} />
      <TextInput label="Vintage" source="data.vintage" />
      <TextInput label="Producer" source="data.producer" />
      <TextInput label="Region" source="data.region" />
      <SelectInput label="Type" source="data.type" choices={[
        { id: 'Red', name: 'Red' },
        { id: 'White', name: 'White' },
        { id: 'Rosé', name: 'Rosé' },
        { id: 'Sparkling', name: 'Sparkling' },
        { id: 'Dessert', name: 'Dessert' },
      ]} />
      <SelectInput label="Style" source="data.style" choices={[
        { id: 'Dry', name: 'Dry' },
        { id: 'Off-dry', name: 'Off-dry' },
        { id: 'Sweet', name: 'Sweet' },
      ]} />
      <TextInput label="Alcohol" source="data.alcohol" />
      <TextInput label="Description" multiline minRows={4} source="data.description" />
      <TextInput
        label="Description (Español)"
        multiline
        minRows={4}
        source="data.translations.es.description"
        helperText="Optional. Shown to consumers whose device language is set to Spanish."
      />
    </>
);

export default BoxWine;